import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ImagePicker from "@components/inputs/imagePicker";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints, AssetUrl } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";

const schema = z.object({
  bannerName: z.string({invalid_type_error: 'Name is required'}).min(1, 'Name is required'),
  sequence: z.union([
    z
      .string()
      .min(1, "Sequence is required")
      .regex(/^\d+$/, "Sequence must be a whole number"),
    z
      .number()
      .int("Sequence must be a whole number")
      .positive("Sequence must be a positive number"),
  ]),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
});

export default function BannerForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(schema)
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [bannerImage, setBannerImage] = useState(null)
  const [bannerImageMobile, setBannerImageMobile] = useState(null)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchBannerMutation.mutate(id)
    }
  },[pageAction])

  const fetchBannerMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getBanner}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, ...data} = res.data
      const createdOnFormatted = formatDateTime(createdOn)
      reset({...data, createdOn: createdOnFormatted})
      setBannerImage({file: null, preview: `${AssetUrl}${data.desktopImagePath}`})
      setBannerImageMobile({file: null, preview: `${AssetUrl}${data.mobileImagePath}`})
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve banner", "error");
    }
  });

  const createBannerMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createBanner, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBanner'] })
      openSnackbar("Successfully created banner", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create banner", "error");
    }
  })

  const updateBannerMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateBanner}/${id}`, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBanner'] })
      openSnackbar("Successfully updated banner", "success");
      fetchBannerMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update banner", "error");
    }
  })

  const onSubmit = () => {
    const missingBanners = [];
    if (!bannerImage) missingBanners.push("desktop banner");
    if (!bannerImageMobile) missingBanners.push("mobile banner");

    if (missingBanners.length > 0) {
      openSnackbar(`Please upload ${missingBanners.join(" and ")}`, "error");
      return;
    }
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    const formData = new FormData();
    // return
    formData.append("bannerName", data?.bannerName);
    formData.append("sequence", data?.sequence);
    formData.append("by", user?.username);
    formData.append("desktopBanner", bannerImage?.file ? bannerImage?.file : null);
    formData.append("mobileBanner", bannerImageMobile?.file ? bannerImageMobile?.file : null);
    if(pageAction === "create")
      createBannerMutation.mutate(formData)
    else {
      formData.append("id", data?.bannerGuid);
      updateBannerMutation.mutate(formData)
    }
  }

  const onBack = () => {
    navigate('/admin/banner-setup', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchBannerMutation.isPending} isError={fetchBannerMutation.isError} errorMessage="Unable to load banner" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Banner Detail</Typography>
            </Grid2>

            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ImagePicker 
                name="desktop"
                label={"Desktop Banner (650x170)"}
                value={bannerImage}
                onChange={setBannerImage}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ImagePicker 
                name="mobile"
                label={"Mobile Banner (400x250)"}
                value={bannerImageMobile}
                onChange={setBannerImageMobile}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="bannerName"
                control={control}
                label="Name"
                disabled={fetchBannerMutation.isPending || fetchBannerMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="sequence"
                control={control}
                label="Sequence"
                disabled={fetchBannerMutation.isPending || fetchBannerMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>
            
            </>}

          </Grid2>
        </Paper>
        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchBannerMutation.isPending || fetchBannerMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit banner?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createBannerMutation.isPending || updateBannerMutation.isPending}
      />
    </div>
  );
}
